import { Observable, tap, throwError } from "rxjs";
import { catchError, finalize } from "rxjs/operators";
import { BaseFilterModel } from "../models/base-filter.model";
import { BaseItemModel } from "../models/base-item.model";
import { BaseListModel } from "../models/base-list.model";
import { BaseApiService } from "./base-api.service";
import { BaseState } from "./base-state.service";
import { BaseDtoModel } from "@webapp/core/abstracts/models/base-dto.model";
import { HttpActions } from "@webapp/core/abstracts/enums/http-actions.enum";
import { HttpErrorResponse } from "@angular/common/http";

export abstract class BaseFacade<
  ItemModel extends BaseItemModel,
  DtoModel extends BaseDtoModel,
  StateModel extends BaseState,
  ApiService extends BaseApiService<ItemModel, DtoModel>
> {
  protected state: StateModel;
  protected apiService: ApiService;

  protected constructor(state: StateModel, apiService: ApiService) {
    this.state = state;
    this.apiService = apiService;
  }

  getAll$(filter?: BaseFilterModel): Observable<BaseListModel<ItemModel>> {
    this.resetInitialState(HttpActions.getAll);

    return this.apiService.getAll$(filter).pipe(
      catchError((response) => {
        return this.handleRequestError(HttpActions.getAll, response);
      }),
      finalize(() => this.handleRequestFinalized(HttpActions.getAll)),
      tap(() => this.handleRequestSuccess(HttpActions.getAll))
    );
  }

  get$(id: unknown): Observable<ItemModel> {
    this.resetInitialState(HttpActions.get);

    return this.apiService.get$(id).pipe(
      catchError((response) => {
        return this.handleRequestError(HttpActions.get, response);
      }),
      finalize(() => this.handleRequestFinalized(HttpActions.get)),
      tap(() => this.handleRequestSuccess(HttpActions.get))
    );
  }

  post$(basePostModel: Partial<ItemModel>): Observable<ItemModel> {
    this.resetInitialState(HttpActions.post);

    return this.apiService.post$(basePostModel).pipe(
      catchError((response) => {
        return this.handleRequestError(HttpActions.post, response);
      }),
      finalize(() => this.handleRequestFinalized(HttpActions.post)),
      tap(() => this.handleRequestSuccess(HttpActions.post))
    );
  }

  put$(id: unknown, basePostModel: Partial<ItemModel>): Observable<ItemModel> {
    this.resetInitialState(HttpActions.put);

    return this.apiService.put$(id, basePostModel).pipe(
      catchError((response) => {
        return this.handleRequestError(HttpActions.put, response);
      }),
      finalize(() => this.handleRequestFinalized(HttpActions.put)),
      tap(() => this.handleRequestSuccess(HttpActions.put))
    );
  }

  patch$(id: unknown, basePostModel: Partial<ItemModel>): Observable<ItemModel> {
    this.resetInitialState(HttpActions.patch);

    return this.apiService.patch$(id, basePostModel).pipe(
      catchError((response) => {
        return this.handleRequestError(HttpActions.patch, response);
      }),
      finalize(() => this.handleRequestFinalized(HttpActions.patch)),
      tap(() => this.handleRequestSuccess(HttpActions.patch))
    );
  }

  delete$(id: unknown): Observable<void> {
    this.resetInitialState(HttpActions.delete);

    return this.apiService.delete$(id).pipe(
      catchError((response) => {
        return this.handleRequestError(HttpActions.delete, response);
      }),
      finalize(() => this.handleRequestFinalized(HttpActions.delete)),
      tap(() => this.handleRequestSuccess(HttpActions.delete))
    );
  }

  resetState(): void {
    this.state.resetState();
  }

  getState(): StateModel {
    return this.state;
  }

  resetInitialState(method: HttpActions): void {
    this.state.setIsLoading(method, true);
    this.state.setIsError(method, null);
    this.state.setIsSuccess(method, false);
  }

  handleRequestError(method: HttpActions, response: HttpErrorResponse): Observable<never> {
    this.state.setIsError(method, response);
    return throwError(response);
  }

  handleRequestFinalized(method: HttpActions): void {
    this.state.setIsLoading(method, false);
  }

  handleRequestSuccess(method: HttpActions): void {
    this.state.setIsSuccess(method, true);
  }
}
